import * as React from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface StaleDataOverlayProps {
  /** Trigger condition. */
  visible: boolean;
  /** Optional reason text — "SignalR disconnected", "Query stale > 5 min". */
  reason?: string;
  /** Optional retry handler. */
  onRetry?: () => void;
  /** If true, dim the underlying widget instead of showing a full overlay. */
  inline?: boolean;
  className?: string;
}

/**
 * Tyndo StaleDataOverlay — overlay for widgets when their data is no longer
 * trustworthy (SignalR disconnected, query exceeded freshness threshold,
 * tenant scope changed).
 *
 * Two modes:
 *   - inline: dim the widget background, no chrome — for compact KPI cards.
 *   - full overlay: centered card with reason + retry — for charts, gauges.
 */
export function StaleDataOverlay({
  visible,
  reason,
  onRetry,
  inline,
  className,
}: StaleDataOverlayProps) {
  if (!visible) return null;

  if (inline) {
    return (
      <span
        // Stale data is safety-relevant: announce assertively so a
        // screen-reader operator is interrupted when values can no longer
        // be trusted, mirroring the full-overlay `role="alert"`.
        role="alert"
        aria-live="assertive"
        className={cn(
          'absolute inset-0 z-20 flex items-center justify-center bg-surface-raised/70 backdrop-blur-[1px]',
          className
        )}
      >
        <span className="inline-flex items-center gap-1.5 rounded-md bg-surface-overlay px-2 py-1 text-[11px] text-muted-foreground border border-border">
          <WifiOff className="h-3 w-3" aria-hidden />
          {reason ?? 'Data stale'}
        </span>
      </span>
    );
  }

  return (
    <div
      role="alert"
      className={cn(
        'absolute inset-0 z-20 flex items-center justify-center bg-surface-raised/80 backdrop-blur-sm',
        className
      )}
    >
      <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface-overlay p-4 shadow-[var(--shadow-md)]">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--alarm-minor)/0.12)] text-[hsl(var(--alarm-minor))]">
          <WifiOff className="h-4 w-4" />
        </span>
        <div className="text-center">
          <p className="text-sm font-semibold text-foreground">Data stream paused</p>
          {reason && (
            <p className="mt-0.5 text-xs text-muted-foreground">{reason}</p>
          )}
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <RefreshCw className="h-3 w-3" />
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
